import { ApiClient } from '../api/client'

const api = new ApiClient()

export interface CartItem {
  productId: string | null
  quantity: number
  price: number
  discountPercent: number
  priceWithDiscount: number
  subtotal: number
  type?: 'PRODUCT' | 'CREDIT' | 'VIP_UPGRADE'
  productName?: string
  customAmount?: number | null
  vipUpgrade?: { fromPlayerVipId: string; toVipDefinitionId: string; permanent: boolean; available: boolean; reason?: string }
}

export interface Cart {
  id?: string
  items: CartItem[]
  total: number
}

export interface AddItemToCartData {
  productId: string
  quantity: number
}

export interface UpdateItemQuantityData {
  quantity: number
}

class CartService {
  /**
   * Obter carrinho do jogador
   */
  async getCart(storeId: string): Promise<Cart> {
    return await api.get<Cart>(`/public/stores/${storeId}/cart`)
  }

  /**
   * Adicionar item ao carrinho
   */
  async addItem(
    storeId: string,
    data: AddItemToCartData,
  ): Promise<Cart> {
    return await api.post<Cart>(
      `/public/stores/${storeId}/cart/items`,
      data,
    )
  }

  /**
   * Atualizar quantidade de um item
   */
  async updateItemQuantity(
    storeId: string,
    productId: string,
    data: UpdateItemQuantityData,
  ): Promise<Cart> {
    return await api.patch<Cart>(
      `/public/stores/${storeId}/cart/items/${productId}`,
      data,
    )
  }

  /**
   * Remover item do carrinho
   */
  async removeItem(storeId: string, productId: string): Promise<void> {
    await api.delete(`/public/stores/${storeId}/cart/items/${productId}`)
  }

  /**
   * Limpar carrinho
   */
  async clearCart(storeId: string): Promise<void> {
    await api.delete(`/public/stores/${storeId}/cart`)
  }

  /**
   * Adicionar item de compra de créditos ao carrinho.
   * Backend valida valor mínimo/máximo e a flag de feature.
   */
  async addCredit(storeId: string, amount: number): Promise<Cart> {
    return await api.post<Cart>(
      `/public/stores/${storeId}/cart/credits`,
      { amount },
    )
  }

  async removeCredit(storeId: string): Promise<void> {
    await api.delete(`/public/stores/${storeId}/cart/credits`)
  }

  /** Upgrade de VIP escolhido em Meus VIPs (um por vez; o servidor confere e calcula o preço). */
  async addVipUpgrade(storeId: string, data: { fromPlayerVipId: string; toVipDefinitionId: string; permanent: boolean }): Promise<{ name: string; price: number }> {
    return await api.post<{ name: string; price: number }>(`/public/stores/${storeId}/cart/vip-upgrade`, data)
  }

  async removeVipUpgrade(storeId: string): Promise<void> {
    await api.delete(`/public/stores/${storeId}/cart/vip-upgrade`)
  }
}

export const cartService = new CartService()
